import { logService } from '@/services/logService.js'
import { ClientLogQueue, CLIENT_LOG_QUEUE_LIMIT } from './clientLogQueue.js'
import { websocketLogContext, websocketLogCodes } from './websocketLogContext.js'

const DEDUP_MS = 5 * 60 * 1000
const fingerprints = new Map()
const queue = new ClientLogQueue((event, signal) => logService.frontendEvent(event.level, event.message, event, { retryCount: 0, signal }))
if (typeof window !== 'undefined') window.addEventListener('online', () => void queue.flush())

function development() {
  return import.meta.env?.DEV === true
}

function safeValue(value, limit) {
  return String(value ?? '').replace(/[?&#].*$/, '').slice(0, limit)
}

function fingerprint(event) {
  return [event.level, event.code, event.message, event.route, event.component,
    event.diagnostics?.incident_id, event.diagnostics?.connection_id, event.diagnostics?.cause,
    event.diagnostics?.sync_result, event.diagnostics?.consumer].join('|')
}

function write(level, code, message, context = {}) {
  context = context && typeof context === 'object' ? context : {}
  const event = {
    level,
    code: safeValue(code || 'frontend_event', 64),
    message: safeValue(message, 512),
    route: safeValue(context.route || globalThis.window?.location?.pathname, 128),
    component: safeValue(context.component, 128),
  }
  if (websocketLogCodes.has(code)) event.diagnostics = websocketLogContext(context.diagnostics)
  if (development()) {
    const method = level === 'ERROR' ? 'error' : level === 'WARNING' ? 'warn' : 'info'
    // The logger is the only production-approved console boundary.
    // eslint-disable-next-line no-console
    console[method](`[${event.code}] ${event.message}`)
  }
  if (level === 'DEBUG' || (level === 'INFO' && !event.diagnostics)) return
  const now = Date.now()
  const key = fingerprint(event)
  if (fingerprints.has(key) && now - fingerprints.get(key) < DEDUP_MS) return
  if (fingerprints.size >= 256) fingerprints.delete(fingerprints.keys().next().value)
  fingerprints.set(key, now)
  queue.enqueue(event)
}

function writeCompat(level, code, message, context) {
  if (message === undefined || typeof message !== 'string') {
    return write(level, 'frontend_log', code, message && typeof message === 'object' ? message : context)
  }
  return write(level, code, message, context)
}

export const clientLogger = {
  debug: (code, message, context) => writeCompat('DEBUG', code, message, context),
  info: (code, message, context) => writeCompat('INFO', code, message, context),
  warn: (code, message, context) => writeCompat('WARNING', code, message, context),
  error: (code, message, context) => writeCompat('ERROR', code, message, context),
  resetSession: () => { fingerprints.clear(); queue.reset() },
  _resetForTests: () => { fingerprints.clear(); queue.reset() },
}

export { CLIENT_LOG_QUEUE_LIMIT }
globalThis.clientLogger = clientLogger
